import { useState, type FormEvent } from "react";
import { MessageCircle, Send } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Input, Select } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { useSendWhatsapp, useTemplates, useTicketWhatsapp } from "./hooks";
import type { WaMessage, WaStatus } from "./api";

const STATUS_TONE: Record<WaStatus, "muted" | "success" | "warning" | "danger"> = {
  pending: "muted",
  sending: "muted",
  sent: "muted",
  delivered: "success",
  read: "success",
  failed: "danger",
  received: "warning",
};

/**
 * WhatsApp conversation on a ticket (staff). Inside the 24-hour service window
 * you can send free text; outside it, WhatsApp only allows a pre-approved
 * template, so the composer switches to a template picker automatically.
 */
export function TicketWhatsappPanel({ ticketId }: { ticketId: string }) {
  const { can } = useAuth();
  const { data } = useTicketWhatsapp(ticketId);
  const send = useSendWhatsapp(ticketId);
  const { data: templates } = useTemplates(can("ticket:update"));

  const [text, setText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [vars, setVars] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  if (!can("ticket:update")) return null;

  const inWindow = data?.window.in_window ?? false;
  const selected = (templates ?? []).find((t) => t.name === templateName);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (inWindow && text.trim()) {
        await send.mutateAsync({ to_number: recipient(), ticket_id: ticketId, text: text.trim() });
        setText("");
      } else if (templateName) {
        await send.mutateAsync({
          to_number: recipient(),
          ticket_id: ticketId,
          template_name: templateName,
          variables: vars,
        });
        setTemplateName("");
        setVars({});
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send.");
    }
  }

  // The number the conversation is with (from an existing message), else blank —
  // the server also resolves it from the ticket's customer.
  function recipient(): string {
    return data?.messages[0]?.phone_number ?? "";
  }

  const canSend = inWindow ? text.trim() !== "" : templateName !== "";

  return (
    <section className="rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MessageCircle className="h-4 w-4 text-primary" aria-hidden="true" />
          WhatsApp
        </div>
        {inWindow ? (
          <Badge tone="success">Window open · {data?.window.minutes_left}m left</Badge>
        ) : (
          <Badge tone="muted">Outside 24h window</Badge>
        )}
      </div>

      <div className="max-h-56 space-y-2 overflow-y-auto p-3">
        {(data?.messages ?? []).length === 0 && (
          <p className="text-xs text-muted-foreground">No WhatsApp messages on this ticket yet.</p>
        )}
        {(data?.messages ?? []).map((m) => (
          <Bubble key={m.id} message={m} />
        ))}
      </div>

      <form onSubmit={submit} className="space-y-2 border-t border-border p-2">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
            {error}
          </div>
        )}

        {inWindow ? (
          <div className="flex items-center gap-2">
            <Input
              aria-label="WhatsApp message"
              placeholder="Type a reply…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button type="submit" aria-label="Send" disabled={!canSend || send.isPending}>
              <Send className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Select
              aria-label="Template"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                setVars({});
              }}
            >
              <option value="">Choose an approved template…</option>
              {(templates ?? [])
                .filter((t) => t.status === "approved")
                .map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
            </Select>
            {selected?.variables?.map((v) => (
              <Input
                key={v}
                aria-label={v}
                placeholder={v}
                value={vars[v] ?? ""}
                onChange={(e) => setVars((s) => ({ ...s, [v]: e.target.value }))}
              />
            ))}
            <div className="flex justify-end">
              <Button type="submit" disabled={!canSend || send.isPending}>
                <Send className="h-4 w-4" aria-hidden="true" />
                Send template
              </Button>
            </div>
          </div>
        )}
      </form>
    </section>
  );
}

function Bubble({ message }: { message: WaMessage }) {
  const mine = message.direction === "outbound";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${
          mine ? "bg-primary text-primary-foreground" : "bg-muted"
        }`}
      >
        <div className="whitespace-pre-wrap break-words">{message.body}</div>
        <div
          className={`mt-0.5 flex items-center gap-1 text-[10px] ${
            mine ? "text-primary-foreground/70" : "text-muted-foreground"
          }`}
        >
          {formatDateTime(message.created_at)}
          {mine && <Badge tone={STATUS_TONE[message.status]}>{message.status}</Badge>}
        </div>
      </div>
    </div>
  );
}
